import { Control, Controller, FieldError, FieldValues, Path } from 'react-hook-form'

import styles from './styles.module.scss'

import Input from '@/components/Input'
import Select, { SelectOption } from '@/components/Select'

type FormFieldInputType = 'text' | 'email' | 'number' | 'date' | 'textarea'

interface BaseFormFieldProps<T extends FieldValues> {
  name: Path<T>
  label: string
  control: Control<T>
  error?: FieldError
  placeholder?: string
  required?: boolean
  disabled?: boolean
  hint?: string
}

interface InputFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type?: FormFieldInputType
  options?: never
}

interface SelectFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: 'select'
  options: SelectOption[]
}

type FormFieldProps<T extends FieldValues> = InputFormFieldProps<T> | SelectFormFieldProps<T>

const FormField = <T extends FieldValues = FieldValues>(props: FormFieldProps<T>) => {
  const { name, label, control, error, placeholder, required = true, disabled = false, hint } = props
  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>
        {label}
        {required && ' *'}
      </label>
      {hint && <span className={styles.hint}>{hint}</span>}
      <Controller
        name={name}
        control={control}
        render={({ field }) =>
          props.type === 'select' ? (
            <Select
              value={field.value as string}
              options={props.options}
              onChange={field.onChange}
              placeholder={placeholder}
              isDisabled={disabled}
            />
          ) : (
            <Input
              type={props.type ?? 'text'}
              value={field.value as string}
              onInput={field.onChange}
              placeholder={placeholder}
              className={styles.input}
              isDisabled={disabled}
            />
          )
        }
      />
      {error && <p className={styles.errorText}>{error.message}</p>}
    </div>
  )
}

export default FormField
